/**
 * After each route change (and on first load): wait for window/fonts, let React + Suspense
 * paint, then wait for images, iframes, and <video> in the document (with timeouts).
 */

function waitForWindowLoad() {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    window.addEventListener("load", resolve, { once: true });
  });
}

function waitForFonts() {
  if (document.fonts && document.fonts.ready) return document.fonts.ready;
  return Promise.resolve();
}

function doubleRequestAnimationFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function waitForDocumentImages(timeoutMs) {
  if (timeoutMs <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);

    const run = () => {
      const imgs = [...document.querySelectorAll("img")].filter((i) => !i.complete);
      if (imgs.length === 0) {
        clearTimeout(timer);
        resolve();
        return;
      }
      let left = imgs.length;
      const done = () => {
        if (--left <= 0) {
          clearTimeout(timer);
          resolve();
        }
      };
      imgs.forEach((img) => {
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    };

    doubleRequestAnimationFrame().then(run);
  });
}

function waitForIframes(timeoutMs) {
  if (timeoutMs <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    const frames = [...document.querySelectorAll("iframe")];
    if (frames.length === 0) {
      clearTimeout(timer);
      resolve();
      return;
    }
    let left = frames.length;
    const done = () => {
      if (--left <= 0) {
        clearTimeout(timer);
        resolve();
      }
    };
    frames.forEach((f) => {
      f.addEventListener("load", done, { once: true });
      f.addEventListener("error", done, { once: true });
    });
  });
}

function waitForHtmlVideos(timeoutMs) {
  if (timeoutMs <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    const videos = [...document.querySelectorAll("video")];
    if (videos.length === 0) {
      clearTimeout(timer);
      resolve();
      return;
    }
    let left = videos.length;
    const done = () => {
      if (--left <= 0) {
        clearTimeout(timer);
        resolve();
      }
    };
    videos.forEach((v) => {
      if (v.readyState >= 2) done();
      else {
        v.addEventListener("loadeddata", done, { once: true });
        v.addEventListener("error", done, { once: true });
      }
    });
  });
}

/**
 * Full sequence for current document (initial load or after client navigation).
 * Uses a single wall-clock budget so we never exceed maxTotalMs.
 */
export function runPageMediaReady(maxTotalMs = 22000) {
  const start = Date.now();
  const deadline = () => Math.max(0, maxTotalMs - (Date.now() - start));

  const imgPass = (cap) => waitForDocumentImages(Math.min(cap, deadline()));
  const iframePass = (cap) => waitForIframes(Math.min(cap, deadline()));
  const videoPass = (cap) => waitForHtmlVideos(Math.min(cap, deadline()));

  return Promise.all([waitForWindowLoad(), waitForFonts()])
    .then(() => doubleRequestAnimationFrame())
    .then(() => new Promise((r) => setTimeout(r, 100)))
    .then(() => imgPass(12000))
    .then(() => new Promise((r) => setTimeout(r, 240)))
    .then(() => imgPass(8000))
    .then(() => iframePass(8000))
    .then(() => videoPass(6000))
    .then(() => new Promise((r) => setTimeout(r, 350)))
    .then(() => imgPass(5000));
}

export function runPageMediaReadyWithCap(maxMs = 22000) {
  return Promise.race([
    runPageMediaReady(maxMs),
    new Promise((resolve) => setTimeout(resolve, maxMs)),
  ]);
}
